import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { founder } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { quietLink } from '../../ui/sections';

/**
 * "Waar ik voor sta" (Paginateksten › Home › Manifest), op blush. Links Sarahs werkwijze in één
 * alinea; rechts waaróm salon en webshop bij elkaar horen, als één verhouding: een klein deel
 * van je resultaat ontstaat in de salon, het grootste deel thuis. De balk vult zich één keer
 * als hij in beeld komt (2 × ≤ 300 ms), daarna staat hij stil.
 *
 * Contrast: ink en muted op blush ≥ 4,9:1; de balk is beeld (klei en salie ≥ 3:1 op blush).
 */
export function HomeManifest() {
  const pages = usePages();
  const reduce = useReducedMotion();
  const copy = pages.home.manifest;
  if (!copy.statement) return null;
  const home = Math.min(100, Math.max(0, Math.round(copy.homeShare)));
  const salonShare = 100 - home;
  const grow = (delay: number) => reduce ? {} : {
    initial: {
      scaleX: 0
    },
    whileInView: {
      scaleX: 1
    },
    viewport: {
      once: true,
      margin: '0px 0px -15% 0px'
    },
    transition: {
      duration: 0.3,
      delay,
      ease
    }
  };
  return <section className="bg-blush py-20 sm:py-24 lg:py-32" aria-labelledby="manifest-title" data-id="element-7397" data-mp="3caoeqm1g9u17">
      <div className="mx-auto grid max-w-content gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7398" data-mp="3h7ewrewtezp9">
        <div className="lg:col-span-7" data-id="element-7399" data-mp="3m4y9ubhs16fm">
          <h2 id="manifest-title" className="text-[14px] text-ink" data-id="element-7400" data-mp="3q8miyym24a2z">
            {fill(copy.eyebrow)}
          </h2>
          <p className="mt-6 font-display text-[1.65rem] font-light leading-[1.3] tracking-[-0.01em] text-ink sm:text-[2.1rem] lg:text-[2.35rem]" data-id="element-7401" data-mp="3v7poek5y8ibh">
            {fill(copy.statement)}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-2" data-id="element-7402" data-mp="20sn18t4dhcbq">
            <p className="font-display text-2xl italic text-ink" data-id="element-7403" data-mp="3442iigmnh6sl">{founder.firstName}</p>
            {copy.cta && <Link to="/over-ons" className={`${quietLink} text-ink`} data-id="element-7404" data-mp="2z5spocoeeia5">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7405" data-mp="3d5adoptvdrig" />
              </Link>}
          </div>
        </div>

        {home > 0 && <figure className="lg:col-span-4 lg:col-start-9 lg:self-end" data-id="element-7406" data-mp="3n2ydhu6c1p7h">
            <p className="font-display text-[5.5rem] font-light leading-none tracking-[-0.03em] text-ink tabular-nums sm:text-[7rem]" data-id="element-7407" data-mp="3n13rsdn8lt2c">
              {home}
              <span className="text-[0.5em] align-top" data-id="element-7408" data-mp="3vugqjw7zfowc">%</span>
            </p>
            <p className="mt-2 text-[15px] text-ink" data-id="element-7409" data-mp="3rteqxea5latu">{fill(copy.homeLabel)}</p>

            {/* De verhouding als balk: klei = salon, salie = thuis. Puur beeld; de getallen staan ernaast. */}
            <div aria-hidden="true" className="mt-8 flex h-2 w-full gap-1" data-id="element-7410" data-mp="4pgvm6jw47za">
              {salonShare > 0 && <motion.span {...grow(0)} style={{
            width: `${salonShare}%`
          }} className="block h-full origin-left bg-clay" data-id="element-7411" data-mp="oba716sgawzp" />}
              <motion.span {...grow(0.12)} style={{
            width: `${home}%`
          }} className="block h-full origin-left bg-sage" data-id="element-7412" data-mp="t927ip75vrfi" />
            </div>
            {salonShare > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-7413" data-mp="jbnt0v4stcpu">
                <span className="text-ink tabular-nums" data-id="element-7414" data-mp="ja2y34eknrrg">{salonShare}%</span> {fill(copy.salonLabel)}
              </p>}

            {copy.accent && <figcaption className="mt-8 border-t border-ink/15 pt-6 text-[15px] leading-relaxed text-ink" data-id="element-7415" data-mp="abra7a53b82p">{fill(copy.accent)}</figcaption>}
          </figure>}
      </div>
    </section>;
}